// src/components/admin/FormAdminPanel.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
  AggregatedConfigs,
  getAggregatedConfigurations,
} from "../../../../services/formsService/AdminFormConfigurationService"; // Adjust path
import { saveSpecificForm } from "../../../../services/formsService/FormBuilderService";
import { useAuth } from "../../../../firebase/AuthProvider"; // Adjust path
import ConfigEditor from "../../../../utilities/formattedDisplayComponents/ConfigEditor"; // Adjust path
import "./formAdminPanelStyles.css";
import Banner from "../../../ui/Banner/Banner";

const FormAdminPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const [aggregatedConfigs, setAggregatedConfigs] =
    useState<AggregatedConfigs | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for editor content
  const [configJson, setConfigJson] = useState("");
  const [rulesJson, setRulesJson] = useState("");
  const [mappingJson, setMappingJson] = useState("");

  // State for initial content to check for changes
  const [initialConfigJson, setInitialConfigJson] = useState("");
  const [initialRulesJson, setInitialRulesJson] = useState("");
  const [initialMappingJson, setInitialMappingJson] = useState("");

  // State for JSON validity
  const [isConfigValid, setIsConfigValid] = useState(true);
  const [isRulesValid, setIsRulesValid] = useState(true);
  const [isMappingValid, setIsMappingValid] = useState(true);

  // State for the unified save operation
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchConfigs = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const authToken = await currentUser.getIdToken();
      const data = await getAggregatedConfigurations(authToken);
      setAggregatedConfigs(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch configurations.");
      setAggregatedConfigs(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (selectedFormId && aggregatedConfigs) {
      const data = aggregatedConfigs[selectedFormId];
      if (data) {
        // Helper to parse, unwrap, and prepare the JSON for the editor
        const processJson = (
          jsonString: string | null | undefined,
          formId: string
        ) => {
          if (!jsonString) {
            return JSON.stringify({ formId }, null, 2);
          }
          try {
            let obj = JSON.parse(jsonString);
            // If the object is wrapped with the formId, unwrap it
            if (obj && typeof obj === "object" && obj[formId]) {
              obj = obj[formId];
            }
            // Ensure the formId is present in the object for schema validation
            obj.formId = formId;
            return JSON.stringify(obj, null, 2);
          } catch (e) {
            console.warn("Failed to parse JSON, returning default.", e);
            return JSON.stringify({ formId }, null, 2); // Return a valid default
          }
        };

        const config = processJson(data.config, selectedFormId);
        const rules = processJson(data.rules, selectedFormId);
        const mapping = processJson(data.mapping, selectedFormId);

        setConfigJson(config);
        setInitialConfigJson(config);
        setRulesJson(rules);
        setInitialRulesJson(rules);
        setMappingJson(mapping);
        setInitialMappingJson(mapping);
      }
    } else {
      // Clear fields when no form is selected
      setConfigJson("");
      setInitialConfigJson("");
      setRulesJson("");
      setInitialRulesJson("");
      setMappingJson("");
      setInitialMappingJson("");
    }
  }, [selectedFormId, aggregatedConfigs]);

  const handleFormSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const formId = event.target.value;
    setSelectedFormId(formId || null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSaveAll = async () => {
    if (!selectedFormId || !currentUser) return;

    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      const definition = configJson.trim() ? JSON.parse(configJson) : {};
      const rules = rulesJson.trim() ? JSON.parse(rulesJson) : {};
      const mapping = mappingJson.trim() ? JSON.parse(mappingJson) : {};

      // The definition/rules objects from the editor already contain the formId.
      // We ensure it's the correct one and pass it to the service.
      const formConfig = {
        definition: { ...definition, formId: selectedFormId },
        rules: { ...rules, formId: selectedFormId },
      };

      const authToken = await currentUser.getIdToken();

      // Call the single save function which wraps the data correctly for the API
      await saveSpecificForm(formConfig, mapping, authToken);

      setSaveSuccess(true);
      await fetchConfigs();

    } catch (err: any) {
      console.error("Error saving configurations:", err);
      setSaveError(err.message || "An unknown error occurred during save.");
    } finally {
      setIsSaving(false);
    }
  };
  
  const isDirty =
    configJson !== initialConfigJson ||
    rulesJson !== initialRulesJson ||
    mappingJson !== initialMappingJson;

  const isAllJsonValid = isConfigValid && isRulesValid && isMappingValid;

  const currentFormData = selectedFormId
    ? aggregatedConfigs?.[selectedFormId]
    : null;

  return (
    <div className="bg-white flex-grow p-5">
      <h2 className="text-xl font-semibold mb-4">
        Form Configuration Admin Panel
      </h2>

      <div className="my-4">
        {saveSuccess && (
          <Banner
            status="success"
            message="All configurations saved successfully!"
            dismissBanner={() => setSaveSuccess(false)}
          />
        )}
        {saveError && (
          <Banner
            status="error"
            message={`Save Error: ${saveError}`}
            dismissBanner={() => setSaveError(null)}
          />
        )}
        <button
          onClick={handleSaveAll}
          disabled={!isDirty || !isAllJsonValid || isSaving}
          className={`px-4 py-2 text-white rounded ${!isDirty || !isAllJsonValid || isSaving
            ? "bg-blue-300 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
            }`}
        >
          {isSaving ? "Saving..." : "Save All Configurations"}
        </button>
      </div>

      {loading && <p>Loading configurations...</p>}
      {error && <p className="text-red-600">Error loading data: {error}</p>}

      {!loading && !error && aggregatedConfigs && (
        <div>
          <div className="mb-5">
            <label htmlFor="form-select" className="mr-2 font-medium">
              Select Form:
            </label>
            <select
              id="form-select"
              value={selectedFormId ?? ""}
              onChange={handleFormSelect}
              className="p-2 border border-gray-300 rounded min-w-[200px]"
            >
              <option value="">-- Select a Form --</option>
              {Object.keys(aggregatedConfigs)
                .sort()
                .map((formId) => (
                  <option key={formId} value={formId}>
                    {formId}
                  </option>
                ))}
            </select>
          </div>

          {selectedFormId && currentFormData && (
            <div className="mt-5">
              <h3 className="text-lg font-semibold mb-3">
                Editing: {selectedFormId}
              </h3>

              <div className="editors-scroll-container pr-0 border-none">
                <ConfigEditor
                  configType="CONFIG"
                  label="Configuration"
                  initialJson={initialConfigJson}
                  onJsonChange={(json, isValid, errors) => {
                    setConfigJson(json);
                    setIsConfigValid(isValid);
                  }}
                  isEditable={true}
                />
                <ConfigEditor
                  configType="RULES"
                  label="Rules"
                  initialJson={initialRulesJson}
                  onJsonChange={(json, isValid, errors) => {
                    setRulesJson(json);
                    setIsRulesValid(isValid);
                  }}
                  isEditable={true}
                />
                <ConfigEditor
                  configType="MAPPING"
                  label="Mapping"
                  initialJson={initialMappingJson}
                  onJsonChange={(json, isValid, errors) => {
                    setMappingJson(json);
                    setIsMappingValid(isValid);
                  }}
                  isEditable={true}
                />
              </div>
            </div>
          )}
          {selectedFormId && !currentFormData && (
            <p className="mt-5 text-yellow-700">
              Configuration data not found for selected form ID: {selectedFormId}
            </p>
          )}
        </div>
      )}
      {!loading && !error && !aggregatedConfigs && (
        <p className="mt-5 text-gray-500">
          No configurations found or failed to load.
        </p>
      )}
    </div>
  );
};

export default FormAdminPanel;


